import type { UserId } from '@maru/protocol'
import { UsersRound } from 'lucide-react'
import { useT } from './i18n.ts'
import { Avatar } from './kit/display.tsx'
import { Skeleton } from './kit/feedback.tsx'
import { PanelHead } from './panel_head.tsx'

export interface Person {
  id: UserId
  name: string
  is_bot: boolean
  online: boolean
  /** 직함. 이름만으로는 누군지 모른다. */
  title?: string | null
  /** 지금 무엇 중인지. 이모지와 글을 이미 합친 한 줄이다. */
  status?: string | null
}

/** 이름 아래 한 줄 — 상태가 직함보다 앞선다(지금의 일이 더 잘 바뀐다). */
function aside_of(p: Person): string | null {
  if (p.status != null && p.status !== '') return p.status
  if (p.title != null && p.title !== '') return p.title
  return null
}

/** 채널에 든 사람 수 — 봇은 세지 않는다(3차 F327). */
export function humans_in(people: readonly Person[] | undefined): number | undefined {
  return people?.filter((p) => !p.is_bot).length
}

/**
 * 채널의 사람들. 켜진 사람이 위, 그 다음 이름순. 봇은 아래 따로 묶는다.
 *
 * 초록불은 색만이 아니라 글자로도 말한다 — 색을 못 보는 사람이 있다. 아바타는 옆에 이름 글자가
 * 있으니 꾸밈이고, 접속 여부는 숨긴 글자(`sr-only`)가 읽힌다.
 */
/**
 * `close` 가 있으면 불러 세운 판이다(좁은 화면) — 닫기를 달고 `data-up` 으로 덮는 자리에 선다.
 * `people` 이 아직 없으면(채널을 막 옮김) 판을 비우지 않고 자리표시를 세운다 — 판이 사라졌다 다시 서면
 * 가운데 글줄 폭이 두 번 바뀌어 읽던 줄이 옆으로 튄다(3차 F322).
 */
export function PeoplePanel({
  people,
  close,
  pick,
}: {
  people: readonly Person[] | undefined
  close?: (() => void) | undefined
  /** 줄을 누르면 그 사람의 쪽지창. 없으면 줄은 글자일 뿐이다. */
  pick?: ((user: UserId) => void) | undefined
}) {
  const t = useT()
  if (people === undefined)
    return (
      <aside className="Pane PeoplePanel" aria-label={t('채널 구성원')} data-up={close && true}>
        <PanelHead
          icon={<UsersRound size={16} aria-hidden />}
          title={t('채널 구성원')}
          close={close}
        />
        <div className="Pane-body" role="status" aria-label={t('불러오는 중')}>
          <ul className="Pane-list">
            {(['1/2', '1/3', '1/2', '1/3'] as const).map((w, i) => (
              <li key={i}>
                <div className="person">
                  <Skeleton shape="circle" size="md" />
                  <Skeleton width={w} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    )
  const sorted = [...people].sort(
    // 접속한 사람 → 이름순. 봇은 아래 따로 묶는다.
    (a, b) => +b.online - +a.online || a.name.localeCompare(b.name),
  )
  const humans = sorted.filter((p) => !p.is_bot)
  const bots = sorted.filter((p) => p.is_bot)
  const row = (p: Person) => {
    const sub = aside_of(p)
    const inner = (
      <>
        <Avatar
          name={p.name}
          user={p.id}
          size="md"
          // 봇은 초록불을 켜지 않는다 — 늘 꺼진 불에 "자리 비움" 을 달면 봇이 쉬는 것처럼 읽힌다(F236).
          {...(p.is_bot ? {} : { presence: p.online ? 'on' : 'off' })}
          decorative
        />
        <span className="person-text">
          <span className="person-top">
            <span className="name">{p.name}</span>
          </span>
          {sub !== null && <span className="person-sub">{sub}</span>}
        </span>
        {!p.is_bot && <span className="sr-only">{p.online ? t('접속 중') : t('자리 비움')}</span>}
      </>
    )
    // 명단은 누구인지 보려고 연다 — 줄이 눌리지 않으면 쪽지창·대화 열기로 갈 길이 글 속 이름뿐이다(3차 F143).
    return (
      <li key={p.id}>
        {pick === undefined ? (
          <div className="person" data-online={p.online || undefined}>
            {inner}
          </div>
        ) : (
          <button
            type="button"
            className="person"
            data-online={p.online || undefined}
            onClick={() => pick(p.id)}
          >
            {inner}
          </button>
        )}
      </li>
    )
  }
  return (
    <aside className="Pane PeoplePanel" aria-label={t('채널 구성원')} data-up={close && true}>
      <PanelHead
        icon={<UsersRound size={16} aria-hidden />}
        title={t('구성원 {n}', { n: humans.length })}
        close={close}
      />
      <div className="Pane-body">
        <ul className="Pane-list">{humans.map(row)}</ul>
        {/* 봇은 사람 수에 세지 않고 아래에 따로 선다 — 거둔 웹훅의 봇까지 사람 사이에 끼면 명단이 봇 목록이 된다(3차 F327). */}
        {bots.length > 0 && (
          <>
            <h4 className="PeoplePanel-group">{t('앱과 봇 {n}', { n: bots.length })}</h4>
            <ul className="Pane-list">{bots.map(row)}</ul>
          </>
        )}
      </div>
    </aside>
  )
}
